import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import RefreshIcon from '@mui/icons-material/Refresh';

import propertiesServices from '../../services/propertiesServices';
import { moment } from '../../utils/dateUtils';

const formatLastSync = (date) => {
  if (!date) return 'never';
  const m = moment(date);
  if (!m.isValid()) return 'never';
  return m.fromNow();
};

const errorMessage = (err, fallback = 'Something went wrong') => {
  return err?.response?.data?.message || err?.response?.data?.errors?.[0]?.message || err?.message || fallback;
};

const PULL_BUTTON_SX = {
  whiteSpace: 'nowrap',
  borderColor: 'rgba(25, 118, 210, 0.5)',
  color: 'primary.main',
};

const CcProjectsDialog = ({ open, onClose, propertyId, onRefreshProperty }) => {
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [attachInput, setAttachInput] = useState('');
  const [attaching, setAttaching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await propertiesServices.listCcProjects(propertyId);
      setProjects(res.data?.projects || []);
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to load CompanyCam projects'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, propertyId]);

  const handleAttach = async () => {
    const trimmed = attachInput.trim();
    if (!trimmed) {
      toast.warn('Enter a CompanyCam project ID or URL');
      return;
    }
    setAttaching(true);
    try {
      const payload = /^\d+$/.test(trimmed) ? { ccProjectId: trimmed } : { ccProjectUrl: trimmed };
      await propertiesServices.attachCcProject(propertyId, payload);
      toast.success('CompanyCam project linked');
      setAttachInput('');
      await load();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to attach project'));
    } finally {
      setAttaching(false);
    }
  };

  const handleDetach = (ccProjectId) => {
    confirm({
      title: 'Detach CompanyCam project?',
      description: 'Photos already imported into this property will remain in the gallery. The link will simply stop syncing new photos until re-attached.',
    })
      .then(async () => {
        try {
          await propertiesServices.detachCcProject(propertyId, ccProjectId);
          toast.success('Project detached');
          await load();
          onRefreshProperty && onRefreshProperty();
        } catch (err) {
          toast.error(errorMessage(err, 'Failed to detach project'));
        }
      })
      .catch(() => {});
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhotoCameraIcon fontSize="small" />
        CompanyCam Projects
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : projects.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
            No CompanyCam projects are linked to this property yet. Attach one below — usually you can find the project URL in the CompanyCam app under «Project Info → Share».
          </Typography>
        ) : (
          <List dense disablePadding>
            {projects.map((p) => (
              <ListItem
                key={p.ccProjectId}
                divider
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Open in CompanyCam">
                      <IconButton size="small" component="a" href={p.ccProjectUrl} target="_blank" rel="noopener noreferrer">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Detach">
                      <IconButton size="small" color="error" onClick={() => handleDetach(p.ccProjectId)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
                sx={{ pr: 12 }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                        {p.ccProjectName || `Project ${p.ccProjectId}`}
                      </Typography>
                      <Chip
                        size="small"
                        label={p.confidence}
                        color={p.confidence === 'exact' ? 'success' : p.confidence === 'partial' ? 'warning' : 'default'}
                        variant="outlined"
                      />
                      {!p.remoteAvailable && (
                        <Chip size="small" color="error" label="not found in CC" variant="outlined" />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Stack direction="row" spacing={1} sx={{ mt: 0.25, color: 'text.secondary', flexWrap: 'wrap' }}>
                      <Typography variant="caption">ID {p.ccProjectId}</Typography>
                      {typeof p.photoCount === 'number' && (
                        <Typography variant="caption">• {p.photoCount} photos in CC</Typography>
                      )}
                      <Typography variant="caption">• Last sync: {formatLastSync(p.lastSyncAt)}</Typography>
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        <Box sx={{ mt: 2, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            Attach existing CompanyCam project by ID or URL
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              fullWidth
              placeholder="123456789  or  https://app.companycam.com/projects/123456789"
              value={attachInput}
              onChange={(e) => setAttachInput(e.target.value)}
              disabled={attaching}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAttach();
              }}
            />
            <Button
              variant="contained"
              startIcon={attaching ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
              onClick={handleAttach}
              disabled={attaching || !attachInput.trim()}
            >
              Attach
            </Button>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={load} startIcon={<RefreshIcon />} disabled={loading}>
          Refresh list
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const CompanyCamPanel = ({ propertyId, refresh }) => {
  const [pulling, setPulling] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const menuOpen = Boolean(menuAnchor);

  const pullPhotos = async () => {
    setPulling(true);
    try {
      const res = await propertiesServices.pullCcPhotos(propertyId);
      const { imported = 0, skipped = 0, failed = 0, scanned = 0, projects = [] } = res.data || {};
      if (imported > 0) {
        toast.success(`Imported ${imported} new photo${imported === 1 ? '' : 's'} from CompanyCam`);
        refresh && refresh();
      } else if (projects.length === 0) {
        toast.info('No CompanyCam projects are linked to this property yet — open «Manage CompanyCam projects…» to attach one');
      } else if (scanned === 0) {
        toast.info('Linked CompanyCam projects have no photos yet');
      } else {
        toast.info(`No new photos in CompanyCam (scanned ${scanned}, already in CRM)`);
      }
      if (failed > 0) {
        toast.warn(`${failed} photo${failed === 1 ? '' : 's'} failed to import — check API logs`);
      }
      // eslint-disable-next-line no-console
      console.log('[CompanyCam] pull result:', { scanned, imported, skipped, failed, projects });
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to pull photos from CompanyCam'));
    } finally {
      setPulling(false);
    }
  };

  return (
    <>
      <ButtonGroup variant="outlined" size="medium">
        <Button
          startIcon={pulling ? <CircularProgress size={14} color="inherit" /> : <CloudDownloadIcon />}
          onClick={pullPhotos}
          disabled={pulling}
          sx={PULL_BUTTON_SX}
        >
          {pulling ? 'Pulling…' : 'Pull from CC'}
        </Button>
        <Button
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={PULL_BUTTON_SX}
          aria-label="CompanyCam options"
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>

      <Menu anchorEl={menuAnchor} open={menuOpen} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setDialogOpen(true);
          }}
        >
          Manage CompanyCam projects…
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            pullPhotos();
          }}
          disabled={pulling}
        >
          Pull photos now
        </MenuItem>
      </Menu>

      <CcProjectsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        propertyId={propertyId}
        onRefreshProperty={refresh}
      />
    </>
  );
};

export default CompanyCamPanel;
